'use client';

import React, { useState } from 'react';
import { Dialog, PulseButton } from 'src/components';
import { useReactivateCompanyMutation } from 'src/api/redux';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';

interface ReactivateCompanyDialogProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  companyName: string;
}

const ReactivateCompanyDialog = ({
  open,
  onClose,
  companyId,
  companyName,
}: ReactivateCompanyDialogProps) => {
  const log = useLogging();
  const { setNotification } = useNotification();
  const [reactivateCompany] = useReactivateCompanyMutation();
  const [isLoading, setIsLoading] = useState(false);

  const handleReactivate = async () => {
    if (!companyId) return;

    try {
      setIsLoading(true);
      await reactivateCompany(companyId).unwrap();
      setNotification({
        title: 'Success',
        message: 'Company reactivated successfully',
        type: 'success',
      });
    } catch (err: any) {
      log.exception('Failed to reactivate company', err);
      setNotification({
        title: 'Error',
        message:
          err?.data?.error_message ||
          err?.message ||
          'Failed to reactivate company',
        type: 'error',
      });
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  return (
    <Dialog
      isOpen={open}
      onClose={onClose}
      showCloseButton={true}
      aria-labelledby="reactivate-company-dialog-title"
      aria-describedby="reactivate-company-dialog-description"
      data-testid="reactivate-company-dialog"
    >
      <Dialog.Header>
        <div
          id="reactivate-company-dialog-title"
          className="text-lg font-medium text-neutral-600"
          data-testid="reactivate-company-dialog-header"
        >
          Reactivate Company
        </div>
      </Dialog.Header>
      <Dialog.Content>
        <div
          className="flex flex-col gap-2 p-2 h-full w-full"
          data-testid="reactivate-company-dialog-content"
        >
          <span
            id="reactivate-company-dialog-description"
            className="font-medium text-neutral-500 p-2"
            role="alert"
          >
            Are you sure you want to reactivate{' '}
            <strong>{companyName}</strong>?
          </span>
        </div>
      </Dialog.Content>
      <Dialog.Actions>
        <div className="flex flex-row gap-2">
          <PulseButton
            variant="outline"
            color="neutral"
            onClick={onClose}
            data-testid="reactivate-company-dialog-cancel"
          >
            Cancel
          </PulseButton>
          <PulseButton
            variant="solid"
            color="success"
            onClick={handleReactivate}
            data-testid="reactivate-company-dialog-confirm"
            aria-label={`Reactivate ${companyName}`}
            loading={isLoading}
          >
            Reactivate
          </PulseButton>
        </div>
      </Dialog.Actions>
    </Dialog>
  );
};

export default ReactivateCompanyDialog;
